/**
 * Shared primitives and the two data hooks every screen uses.
 *
 * Deliberately dumb: no screen owns a colour or a border. Everything visual is a class in
 * `styles.css`, which is what keeps light and dark honest — a component that hard-codes a
 * colour is a component that is wrong in one of the two themes.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { ApiError, api, formatScaled, parseScaled } from "./api";
import type { League, User } from "./types";

export { Link, navigate, useRoute } from "./router";
export { formatScaled, parseScaled, ApiError };

export function classNames(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

// --- layout ---------------------------------------------------------------------------

export function Card({
  title,
  actions,
  children,
  className,
  as = "section",
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  as?: "section" | "article" | "div";
}) {
  const Wrapper = as;
  return (
    <Wrapper className={classNames("card", className)}>
      {(title !== undefined || actions !== undefined) && (
        <header className="card-head">
          {title !== undefined && <h2>{title}</h2>}
          {actions !== undefined && <div className="card-actions">{actions}</div>}
        </header>
      )}
      {children}
    </Wrapper>
  );
}

export function Stack({ children, tight }: { children: ReactNode; tight?: boolean }) {
  return <div className={classNames("stack", tight && "stack-tight")}>{children}</div>
}

export function Row({ children, wrap }: { children: ReactNode; wrap?: boolean }) {
  return <div className={classNames("row", wrap && "row-wrap")}>{children}</div>;
}

export function Empty({
  title,
  note,
  action,
}: {
  title: string;
  note?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {note !== undefined && <p className="muted">{note}</p>}
      {action !== undefined && <div>{action}</div>}
    </div>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <p className="spinner" role="status" aria-live="polite">
      <span className="spinner-dot" aria-hidden /> {label}
    </p>
  );
}

export function ErrorNote({ error, onDismiss }: { error: unknown; onDismiss?: () => void }) {
  const detail =
    error instanceof ApiError ? error.detail : error instanceof Error ? error.message : String(error);
  return (
    <p className="note note-bad" role="alert">
      {detail}
      {onDismiss !== undefined && (
        <button type="button" className="note-dismiss" onClick={onDismiss}>
          Dismiss
        </button>
      )}
    </p>
  );
}

export function Notice({ children }: { children: ReactNode }) {
  return <p className="note note-info">{children}</p>;
}

export function Warning({ children }: { children: ReactNode }) {
  return <p className="note note-warn">{children}</p>;
}

// --- controls -------------------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger";
  busy?: boolean;
};

export function Button({ variant = "ghost", busy, children, disabled, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      className={classNames("btn", `btn-${variant}`, busy && "btn-busy")}
      disabled={disabled ?? busy}
      aria-busy={busy ? true : undefined}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {error !== undefined ? <p className="field-error">{error}</p> : null}
      {hint !== undefined && error === undefined ? <p className="field-hint">{hint}</p> : null}
    </div>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={classNames("input", className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={classNames("input", "select", className)} {...rest}>
      {children}
    </select>
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={classNames("input", "textarea", className)} {...rest} />;
}

export type Align = "left" | "right" | "center";

export function Table({
  head,
  rows,
  align = [],
  caption,
}: {
  head: (string | null)[];
  rows: ReactNode[][];
  align?: Align[];
  caption?: string;
}) {
  const cellAlign = (index: number): Align => align[index] ?? "left";
  return (
    <div className="table-wrap">
      <table className="table">
        {caption !== undefined && <caption>{caption}</caption>}
        <thead>
          <tr>
            {head.map((cell, index) => (
              <th key={`${cell ?? "blank"}-${index}`} className={`align-${cellAlign(index)}`} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className={`align-${cellAlign(cellIndex)}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function List({ items, className }: { items: ReactNode[]; className?: string }) {
  return (
    <ul className={classNames("list", className)}>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "good" | "warn" | "bad" | "info";
}) {
  return <span className={classNames("badge", `badge-${tone}`)}>{children}</span>;
}

export function Meter({ value, max }: { value: number; max: number }) {
  const share = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  return (
    <span className="meter" aria-hidden>
      <span className="meter-fill" style={{ width: `${share * 100}%` }} />
    </span>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string; badge?: number }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          className={classNames("tab", tab.id === active && "tab-active")}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.badge !== undefined && tab.badge > 0 && <span className="tab-badge">{tab.badge}</span>}
        </button>
      ))}
    </div>
  );
}

// --- data hooks -----------------------------------------------------------------------

export type AuthValue = {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthValue>({
  user: null,
  loading: true,
  refresh: async () => {},
  signOut: async () => {},
});

export const AuthProvider = AuthContext.Provider;

export function useAuth(): AuthValue {
  return useContext(AuthContext);
}

/** Loads `/auth/me` once and hands the result to the shell so the nav can react to it. */
export function useAuthProvider(): AuthValue {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user: who } = await api.me();
      setUser(who);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { user, loading, refresh, signOut };
}

export type LeagueState = {
  league: League | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
};

/**
 * League by slug, shared by every screen under one. `reload` is what a mutation calls so
 * `can_manage` and `can_score` cannot go stale after an admin grants a role elsewhere.
 * A reload behind an already-rendered league must not blank the screen: the caller has
 * just written the card the user is looking at, and a spinner there would swallow the
 * save confirmation the click just earned (a UAT user read it as "the button did nothing").
 */
export function useLeague(slug: string | undefined): LeagueState {
  const [league, setLeague] = useState<League | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  // Which slug the on-screen `league` belongs to; only a cold entry may blank the page.
  const shown = useRef<string | null>(null);

  useEffect(() => {
    if (!slug) {
      shown.current = null;
      setLeague(null);
      setLoading(false);
      return;
    }
    if (shown.current !== slug) setLoading(true);
    const controller = new AbortController();
    api
      .league(slug)
      .then((loaded) => {
        shown.current = slug;
        setLeague(loaded);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(cause);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [slug, tick]);

  return { league, loading, error, reload: () => setTick((value) => value + 1) };
}

/** `useAsync` keeps a screen's load/reload/error triple from being written five ways. */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): {
  data: T | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    load()
      .then((value) => {
        if (!controller.signal.aborted) setData(value);
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(cause);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, error, reload: () => setTick((value) => value + 1) };
}

/** Auto-refresh for anything a scorer can change while a spectator is watching. */
export function useInterval(millisecond: number | null, onTick: () => void): void {
  useEffect(() => {
    if (millisecond === null) return;
    const handle = window.setInterval(onTick, millisecond);
    return () => window.clearInterval(handle);
  }, [millisecond, onTick]);
}

export function formatTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
}

export function formatDay(iso: string | null): string {
  if (!iso) return "unscheduled";
  const date = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(date.getTime())
    ? iso
    : new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeZone: "UTC" }).format(date);
}

export function formatStamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}


/**
 * Every sign-up route is `noindex, nofollow`: it is a form for a person, not a page worth
 * crawling, and a join token in a URL would otherwise be handed to a crawler by a link
 * preview. Rendered as a real tag because an SPA has no server render to set it in.
 */
export function useNoindex(): void {
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    return () => tag.remove();
  }, []);
}

export function useQueryParam(name: string): string | null {
  return new URLSearchParams(window.location.search).get(name);
}